import {useEffect, useMemo, useState} from "react";
import React from 'react';
import {Button, DatePicker, Input, message, Modal, Space, Table, TableColumnsType, Tooltip} from "antd";
import { DeviData } from "../../../../types/DeviData";
import { House } from "../../../../types/House";
import { AssignmentData } from "../../../../types/AssignmentData";
import {JobCode} from "../../../../types/JobCodes";
import moment from "moment";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import {
    CheckCircleOutlined,
    CloseCircleOutlined, DatabaseOutlined,
    SaveOutlined,
} from "@ant-design/icons";
import "./FinalApproval.css"
import {getConfig} from "../../../../config";
import axios from "axios";
import {useProjectContext} from "../../../../context/ProjectContext";
import locale from "antd/es/date-picker/locale/de_DE";

const FinalApproval: React.FC<{
    assignmentData: AssignmentData[];
    houses: House[];
    deviData: DeviData[];
    jobCodes: JobCode[];
    groupedData: any[];
    selectedProject: string | undefined;
    setIsGrouped: React.Dispatch<React.SetStateAction<boolean>>;
    setGroupedData: React.Dispatch<React.SetStateAction<any[]>>;
}> = ({ assignmentData, houses, deviData, jobCodes, groupedData, setIsGrouped, setGroupedData, selectedProject }) => {
    dayjs.extend(isSameOrBefore);
    const [isLoading, setIsLoading] = useState(false);
    const formatNumberWithApostrophe = (input: number | string): string => {
        const value = typeof input === 'number' ? input : parseFloat(input);
        if (isNaN(value)) return 'Invalid number';
        const rounded = value.toFixed(2); // z. B. "6303001756.89"
        const [integerPart, decimalPart] = rounded.split('.');
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
        return `${formattedInteger}.${decimalPart}`;
    };
    const formatDateWithType = (dateString: string | undefined, type: string): string => {
        if (!dateString) return "TBD";
        const date = dayjs(dateString, "DD.MM.YYYY");
        return `${date.format("DD.MM.YYYY")} (${type})`;
    };
    const { API_BASE_URL } = getConfig();
    const subtractWeeks = (dateString: string, weeks: number): string => {
        if (dateString === "TBD") return "TBD";
        const date = moment(dateString, "DD.MM.YYYY").subtract(weeks, "weeks");
        return date.format("DD.MM.YYYY");
    };

    useEffect(() => {
        groupTableData();
    }, []);

    const tableData = useMemo(() => {
        return assignmentData.map((assignment, index) => {
            const house = houses.find((h) => h.id === assignment.houseId);
            const section = assignment.sectionName;
            const jobCodeObject = jobCodes.find((jc) => jc.alias === assignment.jobCode);
            const jobCodeAlias = jobCodeObject ? jobCodeObject.descriptionSpez : "Unknown";
            const deviDataObject = deviData.find((d) => d.id === assignment.projectId);
            const assemblyType = jobCodeObject?.assemblyType ?? "Unbekannt";
            const installationLocation = jobCodeObject?.installationLocation?.trim() ? jobCodeObject.installationLocation : "Innen";
            const isPreAssembly = jobCodeObject?.preAssembly === "1";
            let relevanteFrist = "TBD";
            if (house && house.dates) {
                if (isPreAssembly) {
                    if (installationLocation === "Innen" && house.dates.VormontageInnen) {
                        relevanteFrist = formatDateWithType(house.dates.VormontageInnen, "Vormontage (Innen)");
                    } else if (installationLocation === "Aussen" && house.dates.VormontageAussen) {
                        relevanteFrist = formatDateWithType(house.dates.VormontageAussen, "Vormontage (Aussen)");
                    } else if (installationLocation === "Umgebung" && house.dates.VormontageInnen) {
                        relevanteFrist = formatDateWithType(house.dates.VormontageInnen, "Vormontage (Innen)");
                    }
                } else {
                    if ((installationLocation === "Innen" || installationLocation === "Umgebung") && house.dates.Bezug) {
                        relevanteFrist = formatDateWithType(house.dates.Bezug, "Bezug");
                    } else if (installationLocation === "Aussen" && house.dates.Geruest) {
                        relevanteFrist = formatDateWithType(house.dates.Geruest, "Gerüst");
                    }
                }
            }
            const montage = relevanteFrist !== "TBD" ? subtractWeeks(relevanteFrist, 4) : "TBD";
            const bauabschnitt = section !== "Main" ? `${house?.name || "Unknown"} - ${section}` : house?.name || "Unknown";
            const auftragskartenName = `${bauabschnitt} ${jobCodeAlias}`;
            const supplement = house?.supplement;
            const price = deviDataObject?.price
                ? Number(deviDataObject.price.replace(/[^\d.-]/g, ""))
                : 0;
            const amount = price * (assignment.amount ?? 0);
            const formattedAmount = formatNumberWithApostrophe(amount);
            let state = "valid";
            let stateIssues: string[] = [];
            if (relevanteFrist === "TBD") {
                state = "invalid";
                stateIssues.push("Fehlende relevante Frist");
            }
            if (amount > 60000) {
                state = "invalid";
                stateIssues.push("Betrag über 60'000 CHF");
            }
            const measureDate = section?.[0];
            if (!measureDate) {
                state = "invalid";
                stateIssues.push("Kein Messdatum vorhanden");
            }
            const montageFristVormontage =
                installationLocation === "Innen" && house?.dates?.VormontageInnen
                    ? formatDateWithType(house.dates.VormontageInnen, "Vormontage (Innen)")
                    : (installationLocation === "Aussen" || installationLocation === "Umgebung") && house?.dates?.VormontageAussen
                        ? formatDateWithType(house.dates.VormontageAussen, "Vormontage (Aussen)")
                        : undefined;

            const montageFristRegulaer =
                (installationLocation === "Innen" || installationLocation === "Umgebung") && house?.dates?.Bezug
                    ? formatDateWithType(house.dates.Bezug, "Bezug")
                    : installationLocation === "Aussen" && house?.dates?.Geruest
                        ? formatDateWithType(house.dates.Geruest, "Gerüst")
                        : undefined;

            return {
                key: index,
                nummer: index + 1,
                produkt: jobCodeAlias,
                bauabschnitt: bauabschnitt,
                assemblyType: assemblyType,
                auftragskartenName: auftragskartenName,
                supplement: supplement,
                einbauort: installationLocation,
                amount: formattedAmount,
                relevanteFrist: relevanteFrist,
                montage: montage,
                state: state,
                stateIssues: stateIssues,
                measureDate: measureDate,
                preAssembly: jobCodeObject?.preAssembly ?? 0,
                relevanteFristVormontage: montageFristVormontage,
                relevanteFristRegulaer: montageFristRegulaer,
            };
        });
    }, [assignmentData, houses, deviData, jobCodes]);

    const groupTableData = () => {
        const groupedMap = new Map();

        tableData.forEach((item) => {
            const shouldSplit = item.preAssembly === "1";
            const groupVariants = shouldSplit ? ["Vormontage", "Regulär"] : [null];
            groupVariants.forEach((variant) => {
                const suffix = variant ? ` - ${variant}` : "";
                const groupKey = `${item.produkt}-${item.bauabschnitt}${suffix}`;
                const originalAmount = parseFloat(item.amount.replace(/'/g, ""));
                const partialAmount = shouldSplit ? originalAmount / 2 : originalAmount;

                const relevanteFrist = variant === "Vormontage"
                    ? item.relevanteFristVormontage ?? "TBD"
                    : item.relevanteFristRegulaer ?? item.relevanteFrist ?? "TBD";

                if (!groupedMap.has(groupKey)) {
                    const montage = relevanteFrist !== "TBD"
                        ? (variant === "Vormontage"
                            ? relevanteFrist.split(" ")[0]
                            : subtractWeeks(relevanteFrist.split(" ")[0], 4))
                        : "TBD";
                    groupedMap.set(groupKey, {
                        ...item,
                        auftragskartenName: `${item.auftragskartenName}${suffix}`,
                        amount: partialAmount,
                        stateIssues: [],
                        measureDates: [item.measureDate],
                        relevanteFrist: relevanteFrist,
                        montage: montage,
                    });
                } else {
                    const existingGroup = groupedMap.get(groupKey);
                    existingGroup.amount += partialAmount;
                    existingGroup.measureDates.push(item.measureDate);
                }
            });
        });
        const groupedArray = Array.from(groupedMap.values())
            .filter((item) => {
                const numericAmount = parseFloat(item.amount.toString().replace(/'/g, "").replace(",", "."));
                return numericAmount > 0;
            });

// Gruppieren nach Supplement
        const supplementGroups = new Map<number, any[]>();

        groupedArray.forEach((item) => {
            const supplement = Number(item.supplement) || 0;
            if (!supplementGroups.has(supplement)) {
                supplementGroups.set(supplement, []);
            }
            supplementGroups.get(supplement)!.push(item);
        });

// Sortieren der Supplements (0 zuerst, dann 1, ...)
        const sortedSupplements = Array.from(supplementGroups.keys()).sort((a, b) => a - b);

        const finalGroupedArray: any[] = [];
        sortedSupplements.forEach((supplement) => {
            const group = supplementGroups.get(supplement)!;

            // Sortieren innerhalb der Gruppe
            group.sort((a, b) => {
                const produktCompare = (a.produkt || "").localeCompare(b.produkt || "");
                if (produktCompare !== 0) return produktCompare;
                return (a.bauabschnitt || "").localeCompare(b.bauabschnitt || "");
            });

            // Nummerieren innerhalb der Gruppe
            group.forEach((item, index) => {
                let state = "valid";
                let stateIssues: string[] = [];

                if (item.relevanteFrist === "TBD") {
                    state = "invalid";
                    stateIssues.push("Fehlende relevante Frist");
                }
                const numericAmount = parseFloat(item.amount.toString().replace(/'/g, "").replace(",", "."));
                if (numericAmount > 60000) {
                    state = "invalid";
                    stateIssues.push("Betrag über 60'000 CHF");
                }
                if (item.measureDates.some((date: any) => !date)) {
                    state = "invalid";
                    stateIssues.push("Kein Messdatum vorhanden");
                }

                finalGroupedArray.push({
                    ...item,
                    nummer: index + 1,
                    state,
                    stateIssues,
                    key: finalGroupedArray.length,
                    amount: formatNumberWithApostrophe(numericAmount.toFixed(2))
                });
            });
        });

        setIsGrouped(true);
        setGroupedData(finalGroupedArray);
    };

    const columns: TableColumnsType<typeof tableData[number]> = [
        {
            title: "Nummer",
            dataIndex: "nummer",
            key: "nummer",
            width: 60,
            fixed: "left" as const,
        },
        {
            title: "Nachtrag",
            dataIndex: "supplement",
            key: "supplement",
            width: 60,
            fixed: "left" as const,
        },
        {
            title: "Produkt",
            dataIndex: "produkt",
            key: "produkt",
            width: 250,
        },
        {
            title: "Bauabschnitt",
            dataIndex: "bauabschnitt",
            key: "bauabschnitt",
            width: 200,
        },
        {
            title: "Auftragskarten-Name",
            dataIndex: "auftragskartenName",
            key: "auftragskartenName",
            width: 350,
            render: (text, record, index) => (
                <div style={{ paddingRight: "10px" }}>
                    <Input
                        className="ocInput"
                        value={text}
                        maxLength={120}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            setGroupedData((prevData) =>
                                prevData.map((item, idx) =>
                                    idx === index ? { ...item, auftragskartenName: newValue } : item
                                )
                            );
                        }}
                    />
                </div>
            ),
        },
        {
            title: "Einbauort",
            dataIndex: "einbauort",
            key: "einbauort",
            width: 150,
        },
        {
            title: "Relevante Frist",
            dataIndex: "relevanteFrist",
            key: "relevanteFrist",
            width: 150,
        },
        {
            title: "Montage",
            dataIndex: "montage",
            key: "montage",
            width: 150,
            render: (_, record, index) => {
                const relevanteFristDate = record.relevanteFrist !== "TBD"
                    ? dayjs(record.relevanteFrist, "DD.MM.YYYY")
                    : null;
                return (
                    <DatePicker
                        locale={locale}
                        className="date-picker"
                        size="small"
                        format={"DD.MM.YYYY"}
                        value={record.montage !== "TBD" ? dayjs(record.montage, "DD.MM.YYYY") : null}
                        onChange={(_, dateString) => {
                            if (typeof dateString === "string") {
                                const selectedDate = dayjs(dateString, "DD.MM.YYYY");
                                if (!relevanteFristDate || selectedDate.isSameOrBefore(relevanteFristDate)) {
                                    tableData[index].montage = dateString;
                                }
                            }
                        }}
                    />
                );
            },
        },
        {
            title: "Wert (CHF)",
            dataIndex: "amount",
            key: "amount",
            width: 150,
            align: "right",
        },
        {
            title: "Status",
            dataIndex: "state",
            key: "state",
            width: 150,
            align: "right",
            render: (_, record) => {
                return record.state === "valid" ? (
                    <CheckCircleOutlined style={{ color: "green", fontSize: "16px" }} />
                ) : (
                    <Tooltip title={record.stateIssues.join(", ")}>
                        <CloseCircleOutlined style={{ color: "red", fontSize: "16px" }} />
                    </Tooltip>
                );
            },
        },
    ];

    const persistDataToLocalDatabase = async () => {
        setIsLoading(true);
        try {
            const payload = {
                projectNo: selectedProject,
                assignmentData,
                houses,
                deviData,
                jobCodes
            };
            const response = await axios.post(`${API_BASE_URL}/persistDataToLocalDatabase`, payload);
            if (response.status === 200) {
                message.success("Daten erfolgreich zwischengespeichert.");
            } else {
                message.error("Fehler beim Zwischenspeichern.");
            }
        } catch (error) {
            console.error("Error persisting data:", error);
            message.error("Fehler beim Zwischenspeichern.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <div style={{display: "flex", justifyContent: "flex-end"}}>
                <Space style={{marginBottom: 16}}>
                    <Tooltip title="Daten in die Lokale Datenbank zwischenspeichern. Der aktuelle Stand kann später nochmals aufgerufen werden">
                        <Button
                            value="Zwischenspeichern"
                            type="default"
                            icon={<DatabaseOutlined />}
                            onClick={persistDataToLocalDatabase}
                        >
                            Zwischenspeichern
                        </Button>
                    </Tooltip>

                </Space>
            </div>
            <Table
                columns={columns}
                dataSource={groupedData}
                pagination={{pageSize: 100}}
                scroll={{x: 1200, y: 600}}
            />
        </div>
    );
};

export default FinalApproval;
